import { LitElement, html, css, nothing, PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { EtaChangeReviewWrapperStyles } from './eta-change-review-styles'
import './eta-change-chart'
import './eta-details-table'
import './eta-comparison-table'
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model'
import { isOlderThan30Minutes, getEtaComparisonData, getLastUpdatedTime, getMetricsData, has10MinuteDifference, buildRoutingOptions, updateArrivalPilotEtaToRouteGeometry, constructInProgressPayload, postNotificationState, formatEtaDate, RouteMetricsComparison, EtaComparison, getPlanWithNewEta, showDeclineClfAndPostState, stateDialogs} from './helper'
import { VoyageRoutingModel } from '../../../src/models/voyage-routing-model'
import { markPassedAndAddCurrentPosition } from '../../../src/utils/route-utils'
import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager'
import { ScheduleChangeModel, ScheduleChangeStates, ScheduleEtaChangeType } from '../../../src/models/schedule-change-model'
import { VesselLoaderManager } from '../../vessel-loader/src/vessel-loader-manager'
import { AlertType, VesselAlertsModel } from '../../../src/models/vessel-alerts-model'
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons'
import { ISWEATHERROUTINGENABLED } from '../../../src/models/feature-flag-model-impl'
import { VesselNotificationsService } from '../../../src/services/vessel-notifications-service'
import { VoyageImoModel } from '../../../src/models/voyage-imo-model'

let cachedMetrics: RouteMetricsComparison[] | null = null;
let cachedEtaData: EtaComparison[] | null = null;
let cachedNapiId: string | null = null;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const getCurrentVesselImo = () => isVesselOnCloud ? VoyageImoModel!.imo! : (<any>window)["appenv"].vesselIMONumber;
@customElement('eta-change-review-wrapper')
export class EtaChangeReviewWrapper extends LitElement {
    @property({ type: String }) lastUpdatedTime: string =
        new Date().toLocaleString()
    @property({ type: Object }) _currentExecutingPlan: VoyagePlan | null = null
    @property({ type: Object }) _planWithUpdatedEta: VoyagePlan | null = null
    @property({ type: Boolean }) isLoading = true;
    @property({ type: Boolean }) declineFlow = false;
    @property({ type: Boolean }) shouldOptimizeWithoutIntermediatePlan = false;
    @property({ attribute : false}) alert: ScheduleEtaChangeType | null = null;

    private newEta: string = '';
    private newArrivalBerthTime : string | undefined = '';
    private newArrivalPilotTime : string | undefined = '';
    private arrivalTerminalCode: string = '';
    private metrics : RouteMetricsComparison[] = [];
    private etaData : EtaComparison[] = [];
    private declinedTime : string = '';
    private declinedReason : string = '';
    static styles = [EtaChangeReviewWrapperStyles]
   
    constructor(){
        super()
        this.getPlans();
        this.setLatestAlert = this.setLatestAlert.bind(this);
        this.handleBackClick = this.handleBackClick.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
        this.getPlans = this.getPlans.bind(this);
        this.renderDetailsCard = this.renderDetailsCard.bind(this);
        this.loadCardDetails = this.loadCardDetails.bind(this);
        this.reOptimizePlans = this.reOptimizePlans.bind(this);
        this.setLatestAlert = this.setLatestAlert.bind(this);
        this.setLoading = this.setLoading.bind(this);
        this.handleAccept = this.handleAccept.bind(this);
    }

    updated(changedProps: PropertyValues) {
        super.updated(changedProps);

        if (changedProps.has('isLoading')) {
            const eventName = this.isLoading ? 'hide-panel' : 'show-panel';

            this.dispatchEvent(new CustomEvent(eventName, {
            bubbles: true,
            composed: true
            }));
        }
    }

    private async setLatestAlert(): Promise<boolean>{
        this.alert = ScheduleChangeModel!.getCurrentAlert();
        if(!this.alert)
            return false;

        const latestAlert = await VesselNotificationsService.fetchAlert(this.alert.id);

        if(this.alert.state !== ScheduleChangeStates.Declined || latestAlert.state === ScheduleChangeStates.Ended){
            const dialog = stateDialogs[latestAlert.state];

            if (dialog) {
                this.handleBackClick();
                VoyageDialogPopupManager.createDialog({
                    headerText: dialog.header,
                    bodyText: dialog.body,
                    noCloseButton: true, 
                });
                return false;
            }
        }

        if(cachedNapiId !== this.alert.id){
            cachedMetrics = null;
            cachedEtaData = null;
        }
        
        this.newArrivalBerthTime = this.alert.data.schedule.arrivalTimings.array.find((item) => item.type === 'ESTIMATED')?.timestamp;
        this.newArrivalPilotTime = this.alert.data.schedule.arrivalTimings.array.find((item) => item.type === 'ESTIMATED PILOT_BOARDING_PLACE')?.timestamp;

        this._currentExecutingPlan = await VesselPlanningModel!.getExecutingPlan();

        const planBerthTime = this._currentExecutingPlan?.eta ? new Date(this._currentExecutingPlan.eta).getTime() : undefined;
        const newArrivalBerthTimeMs = this.newArrivalBerthTime ? new Date(this.newArrivalBerthTime).getTime() : undefined;

        const onlyPilotTimeIsChanged = planBerthTime && newArrivalBerthTimeMs && newArrivalBerthTimeMs === planBerthTime;

        this.arrivalTerminalCode = this.alert.data.arrival_terminal_code;
        this.newEta = onlyPilotTimeIsChanged ? await formatEtaDate(this.newArrivalPilotTime, this.arrivalTerminalCode) : await formatEtaDate(this.newArrivalBerthTime, this.arrivalTerminalCode);
        this.declineFlow = this.alert.state == ScheduleChangeStates.Declined ? true : false;
        return true;
    }

    private async getPlans() {
        this.setLoading(true);
        const shouldContinue = await this.setLatestAlert();
        if (!shouldContinue) {
            this.setLoading(false);
            return;
        }
        
        VesselLoaderManager.createLoader({
            headerText: `ETA Change Request to ${this.newEta} LT`,
            showCancelButton: true,
            onCancel: this.declineFlow ?  () => this.handleBackClick() : () => this.handleMinimize(),
        });

        try {
            await this.fetchPlans();
            if (this.shouldOptimizeWithoutIntermediatePlan) {
                await this.optimizePlanWithNewEta();
            } else if (this.hasTimeDifference()) {
                await this.reOptimizePlans();
            }

            await this.loadCardDetails();

            if (this.alert?.state === ScheduleChangeStates.Active) {
                await postNotificationState(ScheduleChangeStates.InProgress);
            }
        } finally {
            VesselLoaderManager.removeLoader();
            this.setLoading(false);
        }
    }

    private async fetchPlans() {
        const newPlan = await ScheduleChangeModel!.getNewPlanWithUpdatedEta();
        this._planWithUpdatedEta = newPlan?.optimized_plan || null;

        if (!this._planWithUpdatedEta) {
            this.shouldOptimizeWithoutIntermediatePlan = true;
            console.info('Intermediate Plan not found with updated ETA to show ETA change review');
        }

        this._currentExecutingPlan = await VesselPlanningModel!.getExecutingPlan();
        if (!this._currentExecutingPlan) {
            console.info('No executing plan found to show ETA change review');
            VoyageDialogPopupManager.createDialog({
                headerText: 'Failed to optimize the plans',
                isError: true,
                bodyText: 'Please check if the current voyage plan is approved and Try again.'
            })
            this.handleMinimize();
            return;
        }

        if (this.hasTimeDifference()) {
            this.shouldOptimizeWithoutIntermediatePlan = true;
        }

        if (this.alert?.state == ScheduleChangeStates.Declined) {
            this.declineFlow = true;
            this.shouldOptimizeWithoutIntermediatePlan = true;
            const declinedData = this.alert.notification_responses?.find(response => response.state === ScheduleChangeStates.Declined)?.data;
            this.declinedReason = declinedData?.deviation_reason || '';
            this.declinedTime = await formatEtaDate(declinedData?.deviation_remark_timestamp, this.arrivalTerminalCode);
        }
    }

    private hasTimeDifference(): boolean {
        return has10MinuteDifference(
            this._currentExecutingPlan?.update_dtm || '',
            this._planWithUpdatedEta?.update_dtm || ''
        );
    }

    private async optimizePlanWithNewEta(){
        if(this._currentExecutingPlan){
            this._planWithUpdatedEta = getPlanWithNewEta(this._currentExecutingPlan, this.newArrivalBerthTime, this.newArrivalPilotTime) 

            await this.reOptimizePlans(); // reoptimize both the plans as we don't know when the user might open to accept the declined request

            this._planWithUpdatedEta.update_dtm = new Date().toISOString();
        }
    }

    private async reOptimizePlans() {

        const currentRoute = this._currentExecutingPlan?.routes[this._currentExecutingPlan?.selectedRouteIndex || 0];
        const newRoute = this._planWithUpdatedEta?.routes[this._planWithUpdatedEta?.selectedRouteIndex || 0];
    
        if (!currentRoute || !newRoute) {
            console.info('Selected route not found for current or new plan');
            return;
        }

        const shouldRemoveLoader = !VesselLoaderManager.loaderIsActive();

        if (shouldRemoveLoader) {
            VesselLoaderManager.createLoader({
                headerText: `ETA Change Request to ${this.newEta} LT`,
                showCancelButton: true,
            });
        }
        try{
            const currentOptions = buildRoutingOptions(this._currentExecutingPlan!, currentRoute, true, false);
            const newOptions = {
                ...currentOptions,
                updatePlan: undefined,
                eta: this.newArrivalBerthTime ? new Date(this.newArrivalBerthTime).getTime() : this._planWithUpdatedEta?.eta,
            }
            const isPastETD = (this._currentExecutingPlan?.etd ?? 0) < Date.now();
        
            const updatedCurrentRoute = isPastETD ? await markPassedAndAddCurrentPosition(currentRoute) : currentRoute;
            const updatedNewRoute = updateArrivalPilotEtaToRouteGeometry(this.newArrivalPilotTime!, newRoute);
        
            const [_, recalculatedNewRoute] = await Promise.all([
                VoyageRoutingModel!.postRoutingJobUsingRoute(updatedCurrentRoute, currentOptions, true),
                VoyageRoutingModel!.postRoutingJobUsingRoute(updatedNewRoute, newOptions, true),
            ]);
        
            this._currentExecutingPlan = await VesselPlanningModel!.getExecutingPlan();
            this._planWithUpdatedEta!.update_dtm = new Date().toISOString();
            this._planWithUpdatedEta!.routes[0] = recalculatedNewRoute;

            // Update the new plan
            try{
                ScheduleChangeModel!.updateNewPlanWithUpdatedEta(this._planWithUpdatedEta!);
            } catch (error) {
                console.error('Error updating new plan with updated ETA:', error);
            } 
            this.requestUpdate();
        } catch(err){
            VoyageDialogPopupManager.createDialog({
                headerText: 'Failed to optimize the plans',
                isError: true,
                bodyText: 'Please update the vessel position and route geometry to get the latest optimised data.'
            })
        } finally {
            if (shouldRemoveLoader) {
                VesselLoaderManager.removeLoader();
            }
        }
    }

    private async loadCardDetails(){
        if (!this._currentExecutingPlan || !this._planWithUpdatedEta) return;

        // Skip re-computing if already loaded once and no significant time change
        const shouldUseCache = (cachedNapiId === this.alert?.id) && cachedMetrics && cachedEtaData && !this.hasTimeDifference();

        if (shouldUseCache) {
            this.metrics = cachedMetrics!;
            this.etaData = cachedEtaData!;
            return;
        }

        const [metrics, etaData] = await Promise.all([
            getMetricsData(this._currentExecutingPlan, this._planWithUpdatedEta),
            getEtaComparisonData(this._currentExecutingPlan, this._planWithUpdatedEta, this.arrivalTerminalCode)
        ]);

        this.metrics = metrics;
        this.etaData = etaData;
        cachedMetrics = metrics;
        cachedEtaData = etaData;
        cachedNapiId = this.alert?.id || null;
    }

    private async handleAccept() {

        const isWeatherRoutingFeatureEnabled = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";

        const isWeatherRoutingEnabled = isWeatherRoutingFeatureEnabled && this._currentExecutingPlan?.wr_enabled;

        const dialog = VoyageDialogPopupManager.createDialog({
            headerText: `ETA Change Request to ${this.newEta} LT`,
            bodyText: `Are you sure you would like to accept the ETA changes?`,
            acceptButtonText: 'Yes and Accept',
            rejectButtonText: 'No',
            noCloseButton: true,
            noHeaderBox: true,
        });

        const response = await dialog.getResponse();

        if (response !== 'accept') {
            return;
        }

        try{
            //multiple users might be handling the same alert, so fetch the latest state before proceeding
            const latestAlert = await VesselNotificationsService.fetchAlert(this.alert?.id || '');

            if (this.alert?.state !== ScheduleChangeStates.Declined) {
                const dialog = stateDialogs[latestAlert.state];

                if (dialog) {
                    this.handleBackClick();
                    VoyageDialogPopupManager.createDialog({
                        headerText: dialog.header,
                        bodyText: dialog.body,
                        noCloseButton: true, 
                    });
                    return;
                }
            }

            VesselLoaderManager.createLoader({
                headerText: `ETA Change Request to ${this.newEta} LT`,
                showCancelButton: true
            })

            const newEta = this._planWithUpdatedEta?.eta;
            const oldPlanArrivalPilotEta = this._currentExecutingPlan?.arrivalPilotEta;
            this._currentExecutingPlan = getPlanWithNewEta(this._currentExecutingPlan!, this.newArrivalBerthTime, this.newArrivalPilotTime)
            const currentRoute = this._currentExecutingPlan?.routes[this._currentExecutingPlan?.selectedRouteIndex || 0];
            if (!currentRoute || !newEta) {
                console.info('Selected route or new Eta not found');
                return;
            }
        
            let currentOptions = buildRoutingOptions(this._currentExecutingPlan!, currentRoute, true, true, true);

            if (isWeatherRoutingEnabled) {
                currentOptions = {
                    ...currentOptions,
                    oldPlanArrivalPilotEta : oldPlanArrivalPilotEta
                }
            }

            const isPastETD = (this._currentExecutingPlan?.etd ?? 0) < Date.now();

            const updatedCurrentRoute = isPastETD ? await markPassedAndAddCurrentPosition(currentRoute) : currentRoute;
            const updatedNewRoute = updateArrivalPilotEtaToRouteGeometry(this.newArrivalPilotTime!, updatedCurrentRoute);

            const recalculatedNewRoute = await VoyageRoutingModel!.postRoutingJobUsingRoute(updatedNewRoute, currentOptions, true);

            //updated plan
            this._planWithUpdatedEta!.routes[0] = recalculatedNewRoute;

            VesselLoaderManager.removeLoader();
            this.requestUpdate();

            localStorage.removeItem('RemindLaterView');
            this.handleBackClick() //close the eta modal.

            VesselAlertsModel!.addAlert(getCurrentVesselImo(), {
                title: 'Successfully accepted',
                message: 'You have accepted the ETA changes and speed profiles are updated',
                id: `${this.newEta}`,
                type: AlertType.SuccessAlert,
            });

            await postNotificationState(ScheduleChangeStates.Accepted, this._planWithUpdatedEta)

        } catch (err) {
            VesselLoaderManager.removeLoader();
            this.requestUpdate();
            if(this._currentExecutingPlan)
                VesselPlanningModel?.setPlan(this._currentExecutingPlan?.scheduleId,this._currentExecutingPlan, true);

            VoyageDialogPopupManager.createDialog({
                headerText: 'Failed to optimize the plans',
                isError: true,
                bodyText: 'Plan has been updated with the new eta and the plan will get optimised at the new weather forecast.'
            })
            this.handleMinimize();
            await postNotificationState(ScheduleChangeStates.Accepted)
        } 
    }
    
    private async handleDecline() {
        //multiple users might be handling the same alert, so fetch the latest state before proceeding
        const latestAlert = await VesselNotificationsService.fetchAlert(this.alert?.id || '');

        const dialog = stateDialogs[latestAlert.state];

        if (dialog) {
            this.handleBackClick();
            VoyageDialogPopupManager.createDialog({
                headerText: dialog.header,
                bodyText: dialog.body,
                noCloseButton: true, 
            });
            return;
        }

        const declinePayload = await showDeclineClfAndPostState(this.newEta, AlertType.SuccessAlert);
        if(declinePayload){
            localStorage.removeItem('RemindLaterView');
            this.handleBackClick() //close the eta modal.
            await postNotificationState(ScheduleChangeStates.Declined, null, declinePayload);
        }
    }

    private handleBackClick() {
        this.dispatchEvent(new Event('back-clicked'))
    }

    private handleMinimize() {
        this.dispatchEvent(new Event('minimize'))
    }

    private async handleRefreshClick() {
        cachedMetrics = null;
        cachedEtaData = null;
        cachedNapiId = null;
        //once user has refreshed, cache data should be discarded and fresh data to be loaded
        await this.reOptimizePlans();
        await this.loadCardDetails();
    }

    private setLoading(value: boolean) {
        this.isLoading = value;
    }

    private renderHeader() {
        this.lastUpdatedTime = getLastUpdatedTime(this._planWithUpdatedEta?.update_dtm || new Date().toISOString());
        const shouldEnableRefresh = this._planWithUpdatedEta && isOlderThan30Minutes(this._planWithUpdatedEta?.update_dtm || new Date().toISOString())
        return html`
            <div class="heading">
                <div class="title-wrapper">
                    <span class="title">ETA Change Request to ${this.newEta} LT ${this.declineFlow ? html`<span class="decline">Declined</span>`: nothing}</span>
                    <p class="description">
                        You have a new ETA communication from TbM. Please review
                        all the below informations.
                    </p>
                    <p class="code">Code: ${this.alert?.data.deviation_reason_code} | Details: ${this.alert?.data.deviation_reason}</p>
                </div>
                <div class="heading-status-contents">
                    <div class="last-updated">
                        Last updated on:
                        <div class="color ${shouldEnableRefresh ? 'delay' : ''}">${this.lastUpdatedTime}</div>
                    </div>
                    ${!this.declineFlow ? 
                        html`
                            <mc-button
                                appearance="neutral"
                                fit="small"
                                .disabled=${!shouldEnableRefresh}
                                @click=${this.handleRefreshClick}
                            >
                                Refresh
                            </mc-button>
                            <mc-tooltip position="right-top">
                                <button
                                    class="back-button"
                                    slot="trigger"
                                    @click=${this.handleMinimize}
                                >
                                    <mc-icon icon="shrink"></mc-icon>
                                </button>
                                <span>Minimize ETA Change Request</span>
                            </mc-tooltip>
                        `
                        : nothing
                    }
                </div>
            </div>
        `
    }

    private renderCharts() {
        return html`
            <div class="charts-block">
                ${this.renderChartCard()}
            </div>
        `
    }

    private renderChartCard() {
        return html`
            <div class="chart-card">
                <div class="chart-content">
                    <eta-change-chart .requestedPlan=${this._planWithUpdatedEta}></eta-change-chart>
                </div>
            </div>
        `
    }

    private renderActionSection() {
        this.lastUpdatedTime = getLastUpdatedTime(this._planWithUpdatedEta?.update_dtm || new Date().toISOString());
        const shouldEnableAction = this._planWithUpdatedEta && !isOlderThan30Minutes(this._planWithUpdatedEta?.update_dtm || new Date().toISOString())
        return html`
            <div class="action-section">
                <div class="instructions">
                    Once you have reviewed, choose your action from the below
                    options.
                </div>
                <div class="actions">
                    <mc-button
                        appearance="primary"
                        icon="mi-circle-slash"
                        width="full-width"
                        .disabled=${!shouldEnableAction}
                        @click=${this.handleDecline}
                        ><span class="decline-icon">${SvgIcons.circleSlash}</span>Decline</mc-button
                    >
                    <mc-button
                        appearance="primary"
                        icon="check"
                        width="full-width"
                        .disabled=${!shouldEnableAction}
                        @click=${this.handleAccept}
                        >Accept</mc-button
                    >
                </div>
            </div>
        `
    }

    private renderDeclineActionSection(){
        return html`
            <div class="action-section">
                <div class="warning">
                    <span>This request was declined on: ${this.declinedTime}</span>
                    <span>Reason: ${this.declinedReason}</span>
                </div>
                <div class="instructions">
                    You have the option to re-review it and accept it.
                </div>
                <div class="actions">
                    <mc-button
                        appearance="primary"
                        icon="check"
                        width="full-width"
                        @click=${this.handleAccept}
                        >Accept</mc-button
                    >
                    <mc-button
                        appearance="neutral"
                        width="full-width"
                        @click=${this.handleBackClick}
                        >Close</mc-button
                    >
                </div>
            </div>
        `
    }

    private renderDetailsCard() {
        if (!this.metrics || !this.etaData) return html``;
        return html`
            <div class="details-card">
                <eta-details-table .metrics=${this.metrics}></eta-details-table>
                <div class="eta-actions-section">
                <eta-comparison-table .etaData=${this.etaData}></eta-comparison-table>
                    ${this.declineFlow ? this.renderDeclineActionSection() : this.renderActionSection()}
                </div>
            </div>
        `
    }

    render() {
        if (this.isLoading) {
        return html``;
        }
        return html`
            <div class="eta-change-review">
                ${this.renderHeader()} 
                <div class="eta-change-review-content">
                    ${this.renderCharts()}
                    ${this.renderDetailsCard()}
                </div>
            </div>
        `
    }
}
